import {
  ProductAttribute,
  ProductAttributeKeys,
  ProductConfiguration,
  SizeRunOption,
} from "@wttb/product-configurator";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { parseAsJson, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { GoDash, GoPlus } from "react-icons/go";

import { Input } from "@/common/components/form/input";
import { debounce } from "@/common/utils/debounce";
import { useStoreProduct } from "@/features/product/contexts/product-context";

type SizeRunAttributeProps = {
  attribute: ProductAttribute;
  className?: string;
};

function SizeRunAttribute({ attribute, className }: SizeRunAttributeProps) {
  const t = useTranslations("custom-attributes-forms");
  const { config, setCustomConfigValue, setSkipPriceCalculation } = useStoreProduct();
  const quantity = config![ProductAttributeKeys.Quantity] as number;
  const initialValue = useMemo(() => config![attribute.key], [attribute]) as SizeRunOption;
  const [selectedOptions] = useQueryState<ProductConfiguration & { size?: string }>(
    "selectedOptions",
    parseAsJson<ProductConfiguration & { sizeRun?: SizeRunOption }>().withDefault({}),
  );
  const [sizeRunError, setSizeRunError] = useState(false);
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<SizeRunOption>({
    mode: "onChange",
    defaultValues: initialValue,
  });
  const changeSizeRun = (sizeRun: SizeRunOption, sizeRunQuantity: number, quantity: number) => {
    if (sizeRunQuantity < quantity) {
      setSkipPriceCalculation(true);
      setCustomConfigValue(attribute.key, sizeRun);
    } else {
      setSkipPriceCalculation(false);
      setCustomConfigValue(attribute.key, sizeRun);
      setCustomConfigValue("quantity", sizeRunQuantity);
    }
  };

  const setSizeRunValue = useCallback(debounce(changeSizeRun, 1000), []);

  const inputButtonClick = (valueId: string, valueToAdd: number) => {
    const data = getValues();
    const fieldQty = Number(data[valueId]);
    if (fieldQty === 0 && valueToAdd < 0) return;

    setValue(valueId, fieldQty + valueToAdd);
    handleChange(quantity);
  };

  const handleChange = (quantity: number) => {
    const data = getValues();
    const sizeRunQty = getSizeRunQty();

    const isValuesValid = Object.values(data).some((v) => Number(v) < 0 || isNaN(Number(v)));
    if (sizeRunQty < 0 || isValuesValid) return;

    setSizeRunValue(data, sizeRunQty, quantity);
  };

  const getSizeRunQty = () => {
    const data = getValues();
    return (
      Object.values(data).reduce((acc, d) => Number(acc) + (isNaN(Number(d)) ? 0 : Number(d)), 0) ||
      0
    );
  };

  useEffect(() => {
    const sizeRunQty = getSizeRunQty();
    if (quantity > sizeRunQty || quantity < sizeRunQty || isNaN(quantity)) setSizeRunError(true);
    else setSizeRunError(false);
  }, [quantity, config?.sizeRun]);

  useEffect(() => {
    // Set 0 for all size run values on start if quantity not provided
    // If yes - set qty to standard value
    if (!quantity && !selectedOptions) {
      setSizeRunError(true);
      setValue(attribute.defaultValueId, 0);
    }
  }, []);

  return (
    <div className={className}>
      <div className="flex gap-2">
        <p className="text-grey-90 !mb-4 text-xl font-semibold leading-none">
          {t("text-allocate-your-sizes")}
        </p>
        <p
          className={cn("!mb-4 text-xl font-semibold leading-none", {
            "text-danger": sizeRunError,
            "text-green": !sizeRunError,
          })}
        >
          {t("text-total-quantity", { quantity: quantity || 0 })}
        </p>
      </div>
      <form
        noValidate
        className="grid grid-flow-row grid-cols-2 grid-rows-2 gap-2 md:grid-cols-4 md:grid-rows-1"
      >
        {attribute.values.map((value) => (
          <Input
            key={value.id}
            label={value.display}
            variant="solid"
            type="number"
            {...register(value.id, {
              valueAsNumber: true,
              required: t("error-required"),
              onChange: () => handleChange(quantity),
              validate: {
                isPositive: (v) => Number(v) >= 0 || t("error-positive"),
                min: (v) => Number(v) >= 0 || t("error-min"),
              },
            })}
            error={errors[value.id] && errors[value.id]?.message!}
            className="w-4/5"
            inputClassName={cn("!md:text-base h-12 !text-sm", {
              "!bg-berry-10 !border-[2px] !border-berry-90": Number(getValues(value.id)) > 0,
            })}
            postfixNode={
              <div className="absolute right-[-25px] flex flex-col gap-1 text-center">
                <button type="button" onClick={() => inputButtonClick(value.id, 1)}>
                  <GoPlus size="20px" color="#3b444a" className="bg-grey-40 rounded-full" />
                </button>
                <button type="button" onClick={() => inputButtonClick(value.id, -1)}>
                  <GoDash size="20px" color="#3b444a" className="bg-grey-40 rounded-full" />
                </button>
              </div>
            }
          />
        ))}
      </form>
    </div>
  );
}

export { SizeRunAttribute };
